import { PrimaryEdit } from '@warehouse/graphql';
import { unflattenRegions } from '@warehouse/title/core';

/**
 * Builds the `primaryEdit` payload sent alongside a title on creation.
 *
 * `editClasses` and `versionLanguages` are always sent (even when empty) so the backend
 * can clear them; `editUse`, `madeForRegions` and the edit description are only sent when they hold a value.
 * The form edits a single edit description, sent as the only entry of `descriptions`.
 *
 * `madeForRegions` is stored on the form as a flat list of region uuids
 * (`{ madeForRegions: string[], isInclude }`), but the API expects `CreateTitleV2Region`
 * objects (`{ country } | { countryRegion }`), so we unflatten them here. The form's
 * include/exclude toggle routes the selected regions to either `madeForRegions` (include)
 * or `madeForRegionsExclude` (exclude); it defaults to include.
 */
export function buildPrimaryEdit(data: any): PrimaryEdit {
	const primaryEdit: PrimaryEdit = {
		editClasses: data.editClass ?? [],
		versionLanguages: data.versionLanguages ?? [],
	};

	if (data?.editUse) {
		primaryEdit.editUse = data.editUse;
	}

	const editDescription = data?.editDescription?.trim();
	if (editDescription) {
		primaryEdit.descriptions = [{ description: editDescription }];
	}

	const selectedRegions: string[] | undefined = data?.madeForRegions?.madeForRegions;
	if (selectedRegions?.length) {
		const regions = unflattenRegions(selectedRegions);
		const isInclude = data?.madeForRegions?.isInclude ?? true;
		if (isInclude) {
			primaryEdit.madeForRegions = regions;
		} else {
			primaryEdit.madeForRegionsExclude = regions;
		}
	}

	return primaryEdit;
}

export const WORK_TYPES_WITH_DEFAULT_EDIT = ['Movie', 'Supplemental', 'Episode'];

export function workTypeSupportsDefaultEdit(workTypeName: string | undefined) {
	return !!workTypeName && WORK_TYPES_WITH_DEFAULT_EDIT.includes(workTypeName);
}
